import {
  RowData,
  RowDataReducerState,
  RowDataReducerAction,
} from "../../../../types";
import {
  getUpdatedRowDataListIfTabsExist,
  isSetupRecordActionsRestricted,
  getUpdatedRowDataIfTabsExist,
} from "../../helpers";

/**
 * Orchestrates all reducer dispatches when the active tab changes in
 * DASHBOARD_TABBED_SETUP mode.
 *
 * Row data is stored keyed by tab name inside `resetState` so that
 * "Clear Changes" / "Save" reflect the correct tab's edit state at all times.
 */
export function dispatchDashboardTabbedTabChange({
  nextTabKey,
  index,
  rowData,
  rowDataList,
  resetState,
  isRecordOpen,
  dispatch,
  setSelectedDashboardTabSectionKey,
  isReadOnly,
}: {
  nextTabKey: string;
  index: number;
  rowData: RowData;
  rowDataList: RowData[];
  resetState: RowDataReducerState["resetState"];
  isRecordOpen: boolean;
  dispatch: React.Dispatch<RowDataReducerAction>;
  setSelectedDashboardTabSectionKey: (key: string | undefined) => void;
  isReadOnly: boolean;
}): void {
  setSelectedDashboardTabSectionKey(nextTabKey);

  const {
    rowData: preservedResetRowData = {},
    rowDataList: resetRowDataList = [],
    newEntryRowData: preservedResetNewEntryRowData = {},
  } = resetState ?? {};

  const updatedRowDataList = getUpdatedRowDataListIfTabsExist({
    rowDataList,
    resetRowDataList,
    activeTabIndex: index,
    selectedDashboardTabSectionKey: nextTabKey,
  });

  const isPrivilegedSetup = isSetupRecordActionsRestricted(
    nextTabKey,
    nextTabKey,
  );

  if (isPrivilegedSetup) {
    // Privileged setup: array with a single object only.
    const commonRowData = updatedRowDataList[0] ?? {};

    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowData: commonRowData,
        resetState: {
          newEntryRowData: {
            ...preservedResetNewEntryRowData,
            [nextTabKey]: commonRowData,
          },
          rowData: { ...preservedResetRowData, [nextTabKey]: commonRowData },
        },
      },
    });
  } else {
    const updatedResetNewEntryRowData = getUpdatedRowDataIfTabsExist(
      preservedResetNewEntryRowData,
      nextTabKey,
    );

    if (isRecordOpen) {
      dispatch({
        type: "UPDATE_ROW_DATA_REDUCER_STATE",
        payload: {
          resetState: {
            newEntryRowData: {
              ...preservedResetNewEntryRowData,
              [nextTabKey]: updatedResetNewEntryRowData,
            },
            rowData: { ...preservedResetRowData, [nextTabKey]: rowData },
          },
        },
      });
      return;
    }

    // Defaults to New Entry for non-privileged setup on tab click
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: { rowData: updatedResetNewEntryRowData },
    });
  }

  dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: {
      rowDataErrors: {},
      rowDataList: updatedRowDataList,
      resetState: {
        rowDataList: { ...resetRowDataList, [nextTabKey]: updatedRowDataList },
      },
      isReadOnly,
    },
  });
}

export function dispatchSingleFieldOrSingleSetupRowdata({
  dispatch,
  rowDataList,
  isReadOnly,
}: {
  dispatch: React.Dispatch<RowDataReducerAction>;
  rowDataList: RowData[];
  isReadOnly: boolean;
}): void {
  const updatedRowData = rowDataList[0] ?? {};
  dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: {
      rowDataErrors: {},
      rowData: updatedRowData,
      resetState: {
        rowData: updatedRowData,
        newEntryRowData: updatedRowData,
      },
      isReadOnly,
    },
  });
}
